import React, { useCallback, useEffect, useState } from "react";
import { useNavigate, useParams } from "react-router-dom";
import {
  ArrowLeft,
  CalendarClock,
  Download,
  FilePlus2,
  FileText,
  History,
  Lightbulb,
  Link2,
  Mail,
  Pencil,
  Send,
  ShieldCheck,
  Target,
  User,
  Users,
  Wrench,
} from "lucide-react";
import Layout from "../../layout/Layout";
import { useAuth } from "../../../components/context/AuthContext";
import { getDofIstekById, resendDofMail } from "../../../api/kalite/dofApi";
import DofWorkflowPanel from "./DofWorkflowPanel";
import {
  ASSIGNMENT_LABELS,
  FALIYET_LABELS,
  daysUntil,
  formatDateTime,
  formatDofDate,
  getStatusMeta,
  getTespitTuruMetni,
  initials,
  timeAgo,
} from "./dofFormUtils";
import "./Dof.css";

const ACTION_LABELS = {
  create: "DÖF açıldı",
  update: "Kayıt güncellendi",
  delete: "Silindi",
  save: "Faaliyetler taslak kaydedildi",
  submit: "Onaya gönderildi",
  approve: "Faaliyetler onaylandı",
  reject: "Faaliyetler reddedildi",
  close: "DÖF kapatıldı",
  return: "Etkin bulunmadı, geri gönderildi",
  reopen: "Yeniden açıldı",
  status_change: "Durum elle değiştirildi",
  new_dof: "Yeni DÖF açıldı",
};

const NOTIFICATION_LABELS = {
  assigned: "Atama",
  submitted: "Onaya sunuldu",
  approved: "Onaylandı",
  rejected: "Reddedildi",
  returned: "Geri gönderildi",
  closed: "Kapatma",
  reopened: "Yeniden açıldı",
  reminder: "Hatırlatma",
};

const FAALIYETLER = [
  { type: "duzetme", label: "Düzeltme", icon: Wrench, tanim: "duzetmeFaliyetTanimi", sorumlu: "duzeltmeSorumlu", termin: "duzeltmeTerminSure" },
  { type: "duzeltici", label: "Düzeltici Faaliyet", icon: Target, tanim: "duzelticiFaliyetTanimi", sorumlu: "duzelticiSorumlu", termin: "duzelticiTerminSure" },
  { type: "onleyici", label: "Önleyici Faaliyet", icon: Lightbulb, tanim: "onleyiciciFaliyetTanimi", sorumlu: "onleyiciciSorumlu", termin: "onleyiciciTerminSure" },
];

const getVisibleFaaliyetler = (faliyetTuru) => {
  const duzetme = FAALIYETLER.find((faaliyet) => faaliyet.type === "duzetme");
  const selected = FAALIYETLER.find((faaliyet) => faaliyet.type === faliyetTuru);
  return selected && selected.type !== "duzetme" ? [duzetme, selected] : FAALIYETLER;
};

const REVIZYONLAR = [
  { key: "prosedur", label: "Prosedür" },
  { key: "talimat", label: "Talimat" },
  { key: "isAkisi", label: "İş Akışı" },
  { key: "form", label: "Form" },
];

const ROLE_ORDER = ["sorumlu", "onayci", "takipci", "bilgi"];

const yesNo = (v) => (v === true ? "Evet" : v === false ? "Hayır" : "-");

function Kv({ label, children }) {
  return (
    <div>
      <small>{label}</small>
      <span>{children || "-"}</span>
    </div>
  );
}

function DofDetail() {
  const { id } = useParams();
  const navigate = useNavigate();
  const { user, permissions } = useAuth();

  const [dof, setDof] = useState(null);
  const [loading, setLoading] = useState(true);
  const [mailSending, setMailSending] = useState(false);
  const [pdfGenerating, setPdfGenerating] = useState(false);

  const fetchDof = useCallback(async () => {
    try {
      const response = await getDofIstekById(id);
      if (response?.data?.success) setDof(response.data.data);
      else {
        alert("DÖF kaydı bulunamadı.");
        navigate("/DofList");
      }
    } catch (error) {
      console.error("DÖF detay hatası:", error);
      alert(error.response?.data?.message || "DÖF kaydı getirilemedi.");
      navigate("/DofList");
    } finally {
      setLoading(false);
    }
  }, [id, navigate]);

  useEffect(() => {
    setLoading(true);
    fetchDof();
  }, [fetchDof]);

  const handleResendMail = async () => {
    if (!window.confirm("Sıradaki adımın sorumlusuna bildirim maili tekrar gönderilsin mi?")) return;
    setMailSending(true);
    try {
      const response = await resendDofMail(id);
      alert(response?.data?.message || "Mail gönderildi.");
      fetchDof();
    } catch (error) {
      alert(error.response?.data?.message || "Mail gönderilemedi. Mail ayarlarını kontrol edin.");
    } finally {
      setMailSending(false);
    }
  };

  const handleDownloadPdf = async () => {
    setPdfGenerating(true);
    try {
      const { downloadDofPdf } = await import("./dofPdf");
      await downloadDofPdf(dof);
    } catch (error) {
      console.error("DÖF PDF oluşturma hatası:", error);
      alert("PDF oluşturulurken bir hata oluştu.");
    } finally {
      setPdfGenerating(false);
    }
  };

  if (!user) return null;

  if (loading || !dof) {
    return (
      <Layout>
        <div className="dof-page">
          <div className="dof-loading">
            <div className="dof-spinner" />
            <div>DÖF kaydı yükleniyor...</div>
          </div>
        </div>
      </Layout>
    );
  }

  const access = dof._access || {};
  const status = dof.status || "acik";
  const statusMeta = getStatusMeta(status);
  const people = [...(dof.assignedUsers || [])].sort(
    (a, b) => ROLE_ORDER.indexOf(a.assignmentType) - ROLE_ORDER.indexOf(b.assignmentType)
  );
  const revizyonlar = REVIZYONLAR.filter((r) => dof[r.key] === true);
  const actions = [...(dof.actions || [])].reverse();
  const notifications = [...(dof.notifications || [])].reverse();
  const hasTakip = dof.takipSonucuOneri || dof.takipTarihi || status === "kapali";
  const visibleFaaliyetler = getVisibleFaaliyetler(dof.faliyetTuru);

  return (
    <Layout>
      <div className="dof-page">
        {/* Hero */}
        <section className="dof-hero">
          <div>
            <span className="dof-kicker">
              <ShieldCheck size={15} /> DÖF Kaydı
            </span>
            <h1>
              {dof.dofNo || "DÖF"}{" "}
              <span className={`dof-badge st-${status}`} style={{ verticalAlign: "middle", fontSize: 13 }}>
                {statusMeta.label}
              </span>
            </h1>
            <div className="dof-hero-meta">
              <span>
                <CalendarClock size={14} /> {formatDofDate(dof.dofIstekTarih)}
              </span>
              {dof.faliyetTuru && (
                <span>
                  <FileText size={14} /> {FALIYET_LABELS[dof.faliyetTuru] || dof.faliyetTuru}
                </span>
              )}
              {dof.createdBy?.fullName && (
                <span>
                  <User size={14} /> Açan: {dof.createdBy.fullName}
                </span>
              )}
              {access.myAssignmentType && <span>Rolünüz: {ASSIGNMENT_LABELS[access.myAssignmentType]}</span>}
              {dof.gecmisKayit && <span>Geçmiş DÖF kaydı (mail gönderilmez)</span>}
            </div>
          </div>
          <div className="dof-hero-actions">
            <button type="button" className="dof-btn dof-btn-ghost" onClick={() => navigate("/DofList")}>
              <ArrowLeft size={16} /> Liste
            </button>
            <button type="button" className="dof-btn dof-btn-light" disabled={pdfGenerating} onClick={handleDownloadPdf}>
              <Download size={16} /> {pdfGenerating ? "PDF Hazırlanıyor..." : "PDF İndir"}
            </button>
            {(access.canEdit || access.isCreator) && status !== "kapali" && !dof.gecmisKayit && (
              <button type="button" className="dof-btn dof-btn-ghost" disabled={mailSending} onClick={handleResendMail}>
                <Send size={16} /> {mailSending ? "Gönderiliyor..." : "Hatırlatma Gönder"}
              </button>
            )}
            {access.canEdit && (
              <button type="button" className="dof-btn dof-btn-light" onClick={() => navigate(`/DofEdit/${dof._id}`)}>
                <Pencil size={16} /> {access.fullAccess ? "Tüm Alanları Düzenle" : "Kayıt ve Atamaları Düzenle"}
              </button>
            )}
          </div>
        </section>

        <div className="dof-detail-grid">
          {/* Sol kolon */}
          <div>
            <DofWorkflowPanel dof={dof} access={access} onChanged={fetchDof} />

            <section className="dof-panel">
              <div className="dof-panel-header">
                <h3>
                  <FileText size={17} /> Uygunsuzluk
                </h3>
              </div>
              <div className="dof-panel-body">
                <div className="dof-text-block">{dof.uygunsuzlukTanimi || "Uygunsuzluk tanımı girilmemiş."}</div>
                <div style={{ height: 16 }} />
                <div className="dof-kv">
                  <Kv label="Tespit Türü">{getTespitTuruMetni(dof)}</Kv>
                  <Kv label="Tekrarlayan Uygunsuzluk">{yesNo(dof.tekrarUygunsuzluk)}</Kv>
                  <Kv label="Talep Eden">{dof.dofiTalepEden}</Kv>
                  <Kv label="İstenen Birim Amiri">{dof.dofIstenenBirim}</Kv>
                </div>
                {(dof.kaynakDofId || dof.yeniDofId) && (
                  <div className="dof-callout info" style={{ marginTop: 16, marginBottom: 0 }}>
                    <Link2 size={17} />
                    <div>
                      {dof.kaynakDofId && (
                        <div>
                          Bu DÖF,{" "}
                          <a href={`/DofDetay/${dof.kaynakDofId}`} onClick={(e) => { e.preventDefault(); navigate(`/DofDetay/${dof.kaynakDofId}`); }}>
                            {dof.kaynakDofNo || "kaynak DÖF"}
                          </a>{" "}
                          kaydının faaliyetleri etkin bulunmadığı için açıldı.
                        </div>
                      )}
                      {dof.yeniDofId && (
                        <div>
                          Bu kayıttan yeni DÖF açıldı:{" "}
                          <a href={`/DofDetay/${dof.yeniDofId}`} onClick={(e) => { e.preventDefault(); navigate(`/DofDetay/${dof.yeniDofId}`); }}>
                            {dof.yeniDofNo || "yeni DÖF"}
                          </a>
                        </div>
                      )}
                    </div>
                  </div>
                )}
              </div>
            </section>

            {/* Sorumlu düzenlerken form iş akışı panelinde; burada tekrar gösterme */}
            {!access.actions?.save && (
            <section className="dof-panel">
              <div className="dof-panel-header">
                <h3>
                  <Target size={17} /> Kök Neden ve Faaliyetler
                </h3>
              </div>
              <div className="dof-panel-body">
                <span className="dof-label" style={{ marginTop: 0 }}>
                  Kök Neden
                </span>
                <div className="dof-text-block">{dof.kokNeden || "Henüz kök neden girilmedi."}</div>
                <span className="dof-label">Faaliyetler</span>
                <div className="dof-activity-grid">
                  {visibleFaaliyetler.map((f) => {
                    const Icon = f.icon;
                    const filled = dof[f.tanim] || dof[f.sorumlu] || dof[f.termin];
                    const days = daysUntil(dof[f.termin]);
                    const late = status === "acik" && days !== null && days < 0;
                    return (
                      <div key={f.tanim} className={`dof-activity ${filled ? "" : "empty"}`}>
                        <h4>
                          <Icon size={15} /> {f.label}
                        </h4>
                        <p>{dof[f.tanim] || "Girilmedi"}</p>
                        {filled && (
                          <footer>
                            <span>
                              Sorumlu: <b>{dof[f.sorumlu] || "-"}</b>
                            </span>
                            <span>
                              Termin: <b style={late ? { color: "var(--danger)" } : undefined}>{formatDofDate(dof[f.termin])}</b>
                              {late && " · gecikti"}
                            </span>
                          </footer>
                        )}
                      </div>
                    );
                  })}
                </div>
              </div>
            </section>
            )}

            {hasTakip && (
              <section className="dof-panel">
                <div className="dof-panel-header">
                  <h3>
                    <ShieldCheck size={17} /> Etkinlik Takibi ve Kapatma
                  </h3>
                </div>
                <div className="dof-panel-body">
                  <div className="dof-kv">
                    <Kv label="Takip Tarihi">{formatDofDate(dof.takipTarihi)}</Kv>
                    <Kv label="Takibi Gerçekleştiren">{dof.takibiGerceklestiren}</Kv>
                    <Kv label="Yönetim Temsilcisi">{dof.yonetimTemsilcisi}</Kv>
                    <Kv label="Kapatma Tarihi">{formatDofDate(dof.dofKapatmaTarihiOnay)}</Kv>
                    <Kv label="Yeni DÖF Açılmalı">{yesNo(dof.yeniDofAcilmali)}</Kv>
                  </div>
                  <span className="dof-label">Takip Sonucu &amp; Öneri</span>
                  <div className="dof-text-block">{dof.takipSonucuOneri || "-"}</div>
                  {(revizyonlar.length > 0 || dof.digerRevAciklama) && (
                    <>
                      <span className="dof-label">Doküman Revizyonları</span>
                      <div>
                        {revizyonlar.map((r) => (
                          <span key={r.key} className="dof-chip" style={{ paddingLeft: 10 }}>
                            {r.label}
                            {dof[`${r.key}RevNo`] ? ` · ${dof[`${r.key}RevNo`]}` : ""}
                            {dof[`${r.key}RevTarihi`] ? ` · ${formatDofDate(dof[`${r.key}RevTarihi`])}` : ""}
                          </span>
                        ))}
                        {dof.digerRevAciklama && (
                          <span className="dof-chip" style={{ paddingLeft: 10 }}>
                            {dof.digerRevAciklama}
                            {dof.digerRevNo ? ` · ${dof.digerRevNo}` : ""}
                            {dof.digerRevTarihi ? ` · ${formatDofDate(dof.digerRevTarihi)}` : ""}
                          </span>
                        )}
                      </div>
                    </>
                  )}
                  {access.actions?.reopen === false && status === "kapali" && !dof.yeniDofId && dof.yeniDofAcilmali && permissions.includes("dof_set") && (
                    <div className="dof-actions" style={{ marginTop: 14 }}>
                      <button type="button" className="dof-btn dof-btn-secondary" onClick={() => navigate(`/DofiWizard?kaynak=${dof._id}`)}>
                        <FilePlus2 size={16} /> Bu Kayıttan Yeni DÖF Aç
                      </button>
                    </div>
                  )}
                </div>
              </section>
            )}
          </div>

          {/* Sağ kolon */}
          <div>
            <section className="dof-panel">
              <div className="dof-panel-header">
                <h3>
                  <Users size={17} /> Kişiler
                </h3>
              </div>
              <div className="dof-panel-body">
                <div className="dof-people">
                  {dof.createdBy?.fullName && (
                    <div className="dof-person">
                      <span className="dof-avatar lg">{initials(dof.createdBy.fullName)}</span>
                      <div>
                        <strong>{dof.createdBy.fullName}</strong>
                        <small>{dof.createdBy.email}</small>
                      </div>
                      <span className="dof-badge dof-badge-muted">Açan</span>
                    </div>
                  )}
                  {people.map((p, i) => (
                    <div className="dof-person" key={`${p.email}-${p.assignmentType}-${i}`}>
                      <span className={`dof-avatar lg role-${p.assignmentType}`}>{initials(p.fullName)}</span>
                      <div>
                        <strong>{p.fullName || p.email}</strong>
                        <small>{[p.departman, p.gorev].filter(Boolean).join(" · ") || p.email}</small>
                      </div>
                      <span className={`dof-badge role-${p.assignmentType}`}>{ASSIGNMENT_LABELS[p.assignmentType]}</span>
                    </div>
                  ))}
                  {!people.length && <span className="dof-hint">Atanmış kişi yok.</span>}
                </div>
              </div>
            </section>

            <section className="dof-panel">
              <div className="dof-panel-header">
                <h3>
                  <History size={17} /> Süreç Geçmişi
                </h3>
              </div>
              <div className="dof-panel-body">
                {actions.length ? (
                  <ul className="dof-timeline">
                    {actions.map((a, i) => (
                      <li key={i} className={`t-${a.action}`}>
                        <strong>
                          {ACTION_LABELS[a.action] || a.action}
                          {a.viaFullAccess && <span className="dof-sub" style={{ marginLeft: 6, fontWeight: 600 }}>(tam yetki ile)</span>}
                        </strong>
                        <small>
                          {a.fullName || "-"} · {formatDateTime(a.at)} ({timeAgo(a.at)})
                        </small>
                        {a.note && <p>{a.note}</p>}
                      </li>
                    ))}
                  </ul>
                ) : (
                  <span className="dof-hint">Kayıtlı işlem yok.</span>
                )}
              </div>
            </section>

            <section className="dof-panel">
              <div className="dof-panel-header">
                <h3>
                  <Mail size={17} /> Bildirimler
                </h3>
              </div>
              <div className="dof-panel-body">
                {notifications.length ? (
                  <div className="dof-mail-list">
                    {notifications.map((n, i) => (
                      <div className="dof-mail-item" key={i} title={n.error || ""}>
                        <div>
                          <strong>{n.email}</strong>
                          <small>
                            {NOTIFICATION_LABELS[n.type] || n.type} · {formatDateTime(n.sentAt)}
                          </small>
                        </div>
                        <span className={`dof-badge ${n.success ? "st-kapali" : "st-gecikmis"}`}>
                          {n.success ? "Gönderildi" : "Hata"}
                        </span>
                      </div>
                    ))}
                  </div>
                ) : (
                  <span className="dof-hint">Henüz bildirim gönderilmedi.</span>
                )}
              </div>
            </section>
          </div>
        </div>
      </div>
    </Layout>
  );
}

export default DofDetail;
